import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Lock, Unlock, CheckCircle, XCircle, ChevronRight, ChevronLeft, Plus, Pencil } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

export default function QuestionnairesPage() {
  const { user } = useAuth();
  const isClient = user?.role === 'CLIENT';
  const [questionnaires, setQuestionnaires] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [taking, setTaking] = useState(null); // assignment being taken
  const [currentQ, setCurrentQ] = useState(0);
  const [responses, setResponses] = useState([]);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      const [qRes, aRes] = await Promise.all([
        api.get('/questionnaires'),
        api.get('/questionnaires/assignments/list'),
      ]);
      setQuestionnaires(qRes.data);
      setAssignments(aRes.data);
    };
    fetch().catch(() => {});
  }, []);

  const getStatus = (qId) => {
    const a = assignments.find(a => a.questionnaire_id === qId);
    if (!a) return 'LOCKED';
    return a.status;
  };

  const getAssignment = (qId) => assignments.find(a => a.questionnaire_id === qId);

  const startAssessment = async (assignmentId) => {
    const { data } = await api.get(`/questionnaires/assignments/${assignmentId}`);
    setTaking(data);
    setCurrentQ(0);
    setResponses(data.questionnaire.questions.map(() => ({ answer: null, score: 0 })));
    setResult(null);
  };

  const handleAnswer = (qIdx, optionIdx, score) => {
    const newResp = [...responses];
    newResp[qIdx] = { question_index: qIdx, answer: optionIdx, score };
    setResponses(newResp);
  };

  const submitAssessment = async () => {
    try {
      const { data } = await api.post(`/questionnaires/assignments/${taking._id}/respond`, { responses });
      setResult(data);
      const aRes = await api.get('/questionnaires/assignments/list');
      setAssignments(aRes.data);
    } catch {}
  };

  if (taking && !result) {
    const q = taking.questionnaire;
    const question = q.questions[currentQ];
    const progress = ((currentQ + 1) / q.questions.length) * 100;
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="take-assessment">
        <div>
          <h2 className="text-xl font-semibold text-[#1A3330] font-['Outfit']">{q.title}</h2>
          <p className="text-sm text-[#4A6B68] mt-1">{q.description}</p>
        </div>
        <Progress value={progress} className="h-2 bg-[#E5F2F1]" />
        <p className="text-xs text-[#4A6B68]">Question {currentQ + 1} of {q.questions.length}</p>
        <Card className="bg-white border-[#E5F2F1] rounded-2xl">
          <CardContent className="p-6">
            <p className="text-base font-medium text-[#1A3330] mb-4">{question.question_text}</p>
            <RadioGroup key={`q-${currentQ}`} value={responses[currentQ]?.answer !== null && responses[currentQ]?.answer !== undefined ? responses[currentQ].answer.toString() : ''} onValueChange={v => {
              const idx = parseInt(v);
              handleAnswer(currentQ, idx, question.scores?.[idx] || 0);
            }}>
              {question.options.map((opt, i) => (
                <div key={i} className="flex items-center space-x-3 p-3 rounded-xl hover:bg-[#F4FAF9] transition-colors">
                  <RadioGroupItem value={i.toString()} id={`q${currentQ}-opt-${i}`} />
                  <Label htmlFor={`q${currentQ}-opt-${i}`} className="text-sm text-[#1A3330] cursor-pointer flex-1">{opt}</Label>
                </div>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>
        <div className="flex justify-between">
          <Button variant="ghost" disabled={currentQ === 0} onClick={() => setCurrentQ(currentQ - 1)} data-testid="prev-question-btn"><ChevronLeft className="w-4 h-4 mr-1" />Previous</Button>
          {currentQ < q.questions.length - 1 ? (
            <Button onClick={() => setCurrentQ(currentQ + 1)} disabled={responses[currentQ]?.answer === null} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="next-question-btn">Next<ChevronRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <Button onClick={submitAssessment} disabled={responses.some(r => r.answer === null)} className="bg-[#F5B041] hover:bg-[#E59E29] text-white rounded-xl" data-testid="submit-assessment-btn">Submit Assessment</Button>
          )}
        </div>
        <Button variant="ghost" onClick={() => { setTaking(null); setResult(null); }} className="text-[#4A6B68]" data-testid="cancel-assessment-btn">Cancel</Button>
      </div>
    );
  }

  if (result) {
    return (
      <div className="max-w-md mx-auto text-center space-y-6" data-testid="assessment-result">
        <CheckCircle className="w-16 h-16 text-[#2E8B57] mx-auto" />
        <h2 className="text-2xl font-semibold text-[#1A3330] font-['Outfit']">Assessment Complete</h2>
        <Card className="bg-white border-[#E5F2F1] rounded-2xl">
          <CardContent className="p-6">
            <p className="text-4xl font-bold text-[#0D8B7D] font-['Outfit']">{result.score}</p>
            <p className="text-lg text-[#1A3330] mt-2 font-medium">{result.interpretation}</p>
          </CardContent>
        </Card>
        <Button onClick={() => { setTaking(null); setResult(null); }} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="back-to-catalog-btn">Back to Catalog</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="questionnaires-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-[#1A3330] font-['Outfit']">Questionnaire Catalog</h1>
        {user?.role === 'ADMIN' && (
          <Link to="/questionnaires/builder">
            <Button className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="create-questionnaire-btn"><Plus className="w-4 h-4 mr-2" />Create Questionnaire</Button>
          </Link>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {questionnaires.map(q => {
          const status = isClient ? getStatus(q._id) : 'VIEW';
          const assignment = getAssignment(q._id);
          return (
            <Card key={q._id} className="bg-white border-[#E5F2F1] rounded-2xl hover:shadow-[0_8px_30px_rgba(13,139,125,0.08)] transition-all duration-300">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-base font-semibold text-[#1A3330] font-['Outfit']">{q.title}</h3>
                  <div className="flex items-center gap-1">
                    {user?.role === 'ADMIN' && (
                      <Link to={`/questionnaires/builder/${q._id}`}>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`edit-questionnaire-${q._id}`}><Pencil className="w-3.5 h-3.5 text-[#0D8B7D]" /></Button>
                      </Link>
                    )}
                    {isClient && (
                    <Badge className={`text-[10px] border-0 ${status === 'LOCKED' ? 'bg-[#4A6B68]/10 text-[#4A6B68]' : status === 'ASSIGNED' ? 'bg-[#0D8B7D]/10 text-[#0D8B7D]' : status === 'COMPLETED' ? 'bg-[#2E8B57]/10 text-[#2E8B57]' : 'bg-[#D3544E]/10 text-[#D3544E]'}`}>
                      {status === 'LOCKED' && <Lock className="w-3 h-3 mr-1" />}
                      {status === 'ASSIGNED' && <Unlock className="w-3 h-3 mr-1" />}
                      {status}
                    </Badge>
                    )}
                  </div>
                </div>
                <p className="text-sm text-[#4A6B68] mb-3">{q.description}</p>
                <p className="text-xs text-[#4A6B68]">{q.questions?.length || 0} questions</p>
                {isClient && status === 'ASSIGNED' && assignment && (
                  <div className="mt-3">
                    <p className="text-xs text-[#4A6B68] mb-2">Available until {assignment.deadline_date}</p>
                    <Button size="sm" onClick={() => startAssessment(assignment._id)} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl text-xs" data-testid={`take-assessment-${q._id}`}>Take Assessment</Button>
                  </div>
                )}
                {isClient && status === 'COMPLETED' && assignment && (
                  assignment.score !== null && assignment.score !== undefined ? (
                    <p className="mt-3 text-sm font-medium text-[#2E8B57]">Score: {assignment.score} {assignment.interpretation && `· ${assignment.interpretation}`}</p>
                  ) : (
                    <p className="mt-3 text-xs text-[#4A6B68] italic">Completed on {assignment.completed_at?.split('T')[0] || assignment.assigned_date}</p>
                  )
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
